import { createFileRoute, Link } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/roof-repair-cost-phoenix";
const TITLE = "Roof Repair Cost in Phoenix (2026) | Durafoam Roofing";
const DESCRIPTION =
  "Real Phoenix roof repair pricing for foam, tile, shingle, and flat roofs. What typical fixes actually cost, and how to know if a repair makes sense vs. a recoat.";

const faqs = [
  { q: "How much does a roof repair cost in Phoenix?", a: "Most residential roof repairs fall between $300 and $1,500. Small leak fixes are often $300–$600; multi-area repairs or tile underlayment patches can run $1,000–$2,500+." },
  { q: "Is there a minimum service charge?", a: "Yes — most roofers (us included) have a $300–$450 minimum for a truck roll. That covers the inspection, photos, and a small repair if needed." },
  { q: "When does a repair stop making sense?", a: "Once you're paying $2,000+ in repairs and the roof is past 75% of its service life, it's usually time to talk about a recoat (foam) or R&R (tile). We'll tell you honestly." },
  { q: "Do you give free repair estimates?", a: "Yes. Free roof inspections and written estimates, no obligation. We don't pressure you and we don't invent problems." },
];

export const Route = createFileRoute("/roof-repair-cost-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Roof Repair",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="roof-repair-cost-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Roof Repair Cost in Phoenix"
      subhead="Real 2026 price ranges for the roof repairs Phoenix homeowners actually pay for — foam, tile, shingle, and flat."
      heroBullets={[
        "Small repairs: $300–$600",
        "Typical repairs: $600–$1,500",
        "Major repairs: $1,500–$3,500",
        "Free written estimates",
      ]}
      intro={
        <>
          <p>
            Most Phoenix roof repairs fall in the <strong>$300–$1,500</strong> range. The exact
            number depends on roof type, the size of the problem area, access, and what's causing
            the leak. Single-point fixes (a cracked tile, a failed flashing, a foam puncture) are
            usually quick. Multi-area issues or hidden underlayment damage cost more because the
            work — and the diagnosis — takes longer.
          </p>
          <p>
            We give a flat written price before any work starts. No surprises, no "while we were
            up there" upsells.
          </p>
          <p>
            If a hail or monsoon event caused the damage, insurance may cover it — see{" "}
            <Link to="/storm-damage-repair">storm &amp; hail damage repair</Link>. And if the timing
            is tight, ask us about{" "}
            <Link to="/financing">payment options</Link> before you commit to a quote.
          </p>
        </>
      }
      pricingRows={[
        { label: "Foam — small puncture / bird peck", range: "$300–$550", notes: "Patch & seal" },
        { label: "Foam — multi-area repair", range: "$650–$1,400", notes: "Multiple penetrations" },
        { label: "Tile — cracked / slipped tiles", range: "$300–$700", notes: "10–30 tiles" },
        { label: "Tile — underlayment patch", range: "$1,200–$2,800", notes: "Section R&R" },
        { label: "Shingle — wind damage repair", range: "$400–$1,200", notes: "Replace damaged area" },
        { label: "Flat roof — pinpoint leak", range: "$450–$1,100", notes: "Patch & coat" },
        { label: "Skylight reseal / flashing", range: "$350–$750", notes: "Most residential skylights" },
      ]}
      factors={[
        { title: "Repair size & access", body: "A single tile vs. a section of underlayment is a different job. Multi-story or tight access adds time." },
        { title: "Diagnostic complexity", body: "Some leaks show 10 feet from the source. If we have to chase a leak, it adds inspection time." },
        { title: "Materials", body: "Matching old tile color or sourcing discontinued shingles can add cost vs. in-stock materials." },
        { title: "Warranty work", body: "Workmanship warranty repairs on roofs we installed are free. Repairs on other roofers' work are quoted normally." },
      ]}
      faqs={faqs}
      ctaTitle="Get a real number for your repair"
      ctaBody="Use the calculator for a quick estimate, or schedule a free inspection — we'll find the source, photograph it, and quote the fix."
    />
  );
}
